import * as React from 'react';
import * as Router from 'react-router-dom';
import { EVENTS } from 'analytics/analytics-events';
import { TfActivity, useGetActivity } from 'api';
import { useTranslations } from 'i18n';
import mixpanel from 'mixpanel-browser';
import moment from 'moment-timezone';
import { PERIOD_NAME_REGEX, getSecondsDurationBetweenDates, usePrevious } from 'shared';
import { v4 as uuidv4 } from 'uuid';
import { OverlapAlertIssue } from '../notifications';
import { AthleteCardProps } from '../panels/athlete-card';
import { ACTIVITY_INCLUDES, PeriodTabs, PeriodTabsEnum, checkPeriodOverlap, useGetMappedAthletes } from '../support';
import { AePeriod, SavingState, SavingStateEnum } from './types';
import { Rotation } from 'oas';
import useLocalStorage from 'use-local-storage';
import { difference } from 'lodash';
import { isBefore } from 'date-fns';

export interface ActivityEditorContextShape {
    hasValidPeriods: boolean;
    setHasValidPeriods: React.Dispatch<React.SetStateAction<boolean>>;
    isDirty: boolean;
    setClean: () => void;
    setDirty: () => void;
    activity?: TfActivity;
    periods: AePeriod[];
    setPeriods: (newPeriods: AePeriod[], triggerSave?: boolean) => void;
    handlePeriodCreate: (creationEventType: string, startTime?: Date, endTime?: Date, name?: string) => void;
    handlePeriodDelete: (periodId: string[]) => void;
    selectedDeviceIds: number[];
    setSelectedDeviceIds: (ids: number[]) => void;
    selectedPeriodIds: string[];
    setSelectedPeriodIds: (ids: string[]) => void;
    selectedActivityAthleteIds: string[];
    setSelectedActivityAthleteIds: (ids: string[]) => void;
    selectedPeriodAthleteIds: string[];
    setSelectedPeriodAthleteIds: (ids: string[]) => void;
    athleteIdsInSelectedPeriods: string[];
    athletePanelCards: AthleteCardProps[];
    setAthletePanelCards: (newAthletePanelCard: AthleteCardProps[]) => void;
    athletePanelCardIssues: OverlapAlertIssue[];
    setAthletePanelCardIssues: (issues: OverlapAlertIssue[]) => void;
    lastModified?: number;
    savingState: SavingStateEnum;
    setSavingState: (newState: SavingStateEnum) => void;
    activePeriodTab: PeriodTabsEnum;
    setActivePeriodTab: (newTab: string) => void;
}

const noProviderError = () => {
    console.error('No ActivityEditorProvider is present');
};

const ActivityEditorContext = React.createContext<ActivityEditorContextShape>({
    hasValidPeriods: true,
    isDirty: false,
    setClean: noProviderError,
    setDirty: noProviderError,
    setHasValidPeriods: () => true,
    periods: [],
    setPeriods: noProviderError,
    handlePeriodCreate: noProviderError,
    handlePeriodDelete: noProviderError,
    selectedDeviceIds: [],
    setSelectedDeviceIds: noProviderError,
    selectedPeriodIds: [],
    setSelectedPeriodIds: noProviderError,
    selectedActivityAthleteIds: [],
    setSelectedActivityAthleteIds: noProviderError,
    selectedPeriodAthleteIds: [],
    setSelectedPeriodAthleteIds: noProviderError,
    athleteIdsInSelectedPeriods: [],
    athletePanelCards: [],
    setAthletePanelCards: noProviderError,
    athletePanelCardIssues: [],
    setAthletePanelCardIssues: noProviderError,
    lastModified: undefined,
    savingState: SavingState.none,
    setSavingState: noProviderError,
    activePeriodTab: PeriodTabs.timeline,
    setActivePeriodTab: noProviderError,
});

interface ActivityEditorProviderProps {
    activityId: string;
}
export const ActivityEditorProvider: React.FunctionComponent<ActivityEditorProviderProps> = ({
    activityId,
    children,
}) => {
    const history = Router.useHistory();
    const [lastModified, setLastModified] = React.useState<number>();
    const [periods, actualSetPeriods] = React.useState<AePeriod[]>([]);
    const [athletePanelCards, setAthletePanelCards] = React.useState<AthleteCardProps[]>([]);
    const [selectedPeriodIds, actualSetSelectedPeriodIds] = React.useState<string[]>([]);
    const [selectedDeviceIds, actualSetSelectedDeviceIds] = React.useState<number[]>([]);
    const [selectedActivityAthleteIds, actualSetSelectedActivityAthleteIds] = React.useState<string[]>([]);
    const [selectedPeriodAthleteIds, actualSetSelectedPeriodAthleteIds] = React.useState<string[]>([]);
    const [athleteIdsInSelectedPeriods, setAthleteIdsInSelectedPeriods] = React.useState<string[]>([]);
    const [isDirty, setIsDirty] = React.useState<boolean>(false);
    const [hasValidPeriods, setHasValidPeriods] = React.useState<boolean>(false);
    const [athletePanelCardIssues, setAthletePanelCardIssues] = React.useState<OverlapAlertIssue[]>([]);
    const [savingState, setSavingState] = React.useState<SavingStateEnum>(SavingState.none);
    const [activePeriodTab, actualSetActivePeriodTab] = React.useState<PeriodTabsEnum>(PeriodTabs.timeline);

    // Hooks
    const { __ } = useTranslations();
    const { data: activity, isLoadingError } = useGetActivity(activityId, ACTIVITY_INCLUDES);
    const mappedAthletes = useGetMappedAthletes(activity?.id || '');
    const [selectedPeriodTabView, setSelectedPeriodTabView] = useLocalStorage<string>(
        'selected-tab',
        PeriodTabs.timeline,
    );

    const setClean = React.useCallback(() => {
        setIsDirty(false);
    }, []);

    const setDirty = React.useCallback(() => {
        setIsDirty(true);
    }, []);

    React.useEffect(() => {
        const newAthleteIdsInSelectedPeriods = periods.flatMap((period) =>
            selectedPeriodIds.includes(period.id) ? period.athleteIds : [],
        );

        setAthleteIdsInSelectedPeriods(newAthleteIdsInSelectedPeriods);

        // Ensure only athletes that are in the selected periods remain selected
        actualSetSelectedPeriodAthleteIds((previousSelectedPeriodAthleteIds) =>
            previousSelectedPeriodAthleteIds.filter((id) => newAthleteIdsInSelectedPeriods.includes(id)),
        );
    }, [periods, selectedPeriodIds]);

    React.useEffect(() => {
        actualSetActivePeriodTab(selectedPeriodTabView as PeriodTabsEnum);
    }, [selectedPeriodTabView]);

    const setPeriods = React.useCallback(
        (newPeriods: AePeriod[], triggerSave = true) => {
            // Adjust rotations start and end times so they are always within the period
            const newerPeriods = newPeriods.map((newPeriod) => {
                const oldPeriod = periods.find((p) => p.id === newPeriod.id);
                const periodAthletes = [
                    ...newPeriod.periodAthletes.map((periodAthlete) => {
                        const newRotations: Rotation[] = [];

                        // Adjust rotation start and end times so they are always the period
                        periodAthlete.rotations?.forEach((oldRotation) => {
                            const newRotation = { ...oldRotation };

                            // If rotation start time was the same as the period start time, update it along with the period start time
                            if (oldPeriod !== undefined) {
                                if (oldRotation.start_time_ms === oldPeriod.startDateTime.valueOf()) {
                                    newRotation.start_time_ms = newPeriod.startDateTime.valueOf();
                                }

                                // If rotation end time was the same as the period end time, update it along with the period end time
                                if (oldRotation.end_time_ms === oldPeriod.endDateTime.valueOf()) {
                                    newRotation.end_time_ms = newPeriod.endDateTime.valueOf();
                                }
                            }

                            // If the rotation start time is before the period start time, set it to the period start time
                            if (newRotation.start_time_ms < newPeriod.startDateTime.valueOf()) {
                                newRotation.start_time_ms = newPeriod.startDateTime.valueOf();
                            }

                            // If the rotation end time is after the period end time, set it to the period end time
                            if (newRotation.end_time_ms > newPeriod.endDateTime.valueOf()) {
                                newRotation.end_time_ms = newPeriod.endDateTime.valueOf();
                            }

                            // Only keep the rotation if it overlaps with the period
                            if (
                                newRotation.start_time_ms <= newPeriod.endDateTime.valueOf() &&
                                newRotation.end_time_ms >= newPeriod.startDateTime.valueOf()
                            ) {
                                newRotations.push(newRotation);
                            }
                        });

                        return {
                            ...periodAthlete,
                            rotations: newRotations,
                        };
                    }),
                ];

                return {
                    ...newPeriod,
                    periodAthletes,
                };
            });

            actualSetPeriods(newerPeriods);

            if (triggerSave) {
                // Set a timestamp to track when the activity was last modified
                setLastModified(Date.now());
            }
        },
        [periods],
    );

    const setSelectedDeviceIds = React.useCallback((ids: number[]) => {
        actualSetSelectedDeviceIds(ids);
        actualSetSelectedActivityAthleteIds([]);
        actualSetSelectedPeriodAthleteIds([]);
    }, []);

    const setSelectedActivityAthleteIds = React.useCallback((ids: string[]) => {
        actualSetSelectedDeviceIds([]);
        actualSetSelectedActivityAthleteIds(ids);
        actualSetSelectedPeriodAthleteIds([]);
        setAthletePanelCardIssues([]);
    }, []);

    const setSelectedPeriodAthleteIds = React.useCallback((ids: string[]) => {
        actualSetSelectedDeviceIds([]);
        actualSetSelectedActivityAthleteIds([]);
        actualSetSelectedPeriodAthleteIds(ids);
    }, []);

    const setSelectedPeriodIds = React.useCallback(
        (ids: string[]) => {
            // Log when user click on select all periods
            // ** If we every implement a multi selection of periods we need to change this
            if (difference(ids, selectedPeriodIds).length > 1) {
                mixpanel.track(EVENTS.editing.selectAllPeriods, {
                    number_of_periods_selected: ids.length,
                    view: activePeriodTab,
                });
            }
            actualSetSelectedPeriodIds(ids);
        },
        [selectedPeriodIds, activePeriodTab],
    );

    const setActivePeriodTab = React.useCallback(
        (newTab: string) => {
            mixpanel.track(EVENTS.editing.webEditorViewSelected, { view: newTab });
            // Update context active selected tab
            actualSetActivePeriodTab(newTab as PeriodTabsEnum);
            // Update local storage selected tab
            setSelectedPeriodTabView(newTab);
        },
        [setSelectedPeriodTabView],
    );

    const calculatePeriodTime = React.useCallback(
        (activityStartTime: Date, bufferTimeSecs: number) =>
            periods.length !== 0
                ? moment(periods[periods.length - 1].endDateTime)
                      .add(bufferTimeSecs, 's')
                      .toDate()
                : moment(activityStartTime).add(bufferTimeSecs, 's').toDate(),
        [periods],
    );

    const handlePeriodCreate = React.useCallback(
        (creationEventType: string, startTime?: Date, endTime?: Date, name?: string) => {
            if (activity) {
                const periodStartTime = startTime?.getDate ? startTime : calculatePeriodTime(activity.start_time, 1); // start 1s after
                const periodEndTime = endTime?.getDate
                    ? endTime
                    : calculatePeriodTime(activity.start_time, 15 * 60 + 1); // 15min 1s after

                const newPeriod: AePeriod = {
                    id: uuidv4(),
                    name: name ? name : 'Period ' + (periods.length + 1),
                    startDateTime: periodStartTime,
                    endDateTime: periodEndTime,
                    duration: getSecondsDurationBetweenDates(periodStartTime, periodEndTime),
                    athleteIds: [],
                    periodAthletes: [],
                    athleteCount: 0,
                    activityId: activity.id,
                };

                setPeriods([...periods, newPeriod]);
                setSelectedPeriodIds([...selectedPeriodIds, newPeriod.id]);
                setDirty();
                mixpanel.track(EVENTS.editing.periodCreated, {
                    creationEventType,
                    id: newPeriod.id,
                    name: newPeriod.name,
                    startDateTime: newPeriod.startDateTime,
                    endDateTime: newPeriod.endDateTime,
                });
            }
        },
        [activity, calculatePeriodTime, periods, setPeriods, setDirty, setSelectedPeriodIds, selectedPeriodIds],
    );

    const handlePeriodDelete = React.useCallback(
        (deletePeriodIds: string[]) => {
            (async () => {
                if (
                    await confirm(
                        __(
                            selectedPeriodIds.length === 1
                                ? 'timeline.del_period_confim'
                                : 'timeline.del_multiple_period_confirm',
                        ),
                    )
                ) {
                    // update periods state
                    setPeriods(periods.filter((period) => !deletePeriodIds.includes(period.id)));
                    setSelectedPeriodIds(deletePeriodIds.filter((id) => !deletePeriodIds.includes(id)));
                    setDirty();
                    mixpanel.track(EVENTS.editing.periodDelete, {
                        periods_deleted: deletePeriodIds.length,
                        view: activePeriodTab,
                    });
                }
            })();
        },
        [__, periods, selectedPeriodIds, activePeriodTab, setDirty, setPeriods, setSelectedPeriodIds],
    );

    // Populate periods array after stuff has loaded
    React.useEffect(() => {
        // Only repopulate periods if the current periods are from a different activity
        if (activity?.periods && mappedAthletes && periods?.[0]?.activityId !== activity?.id) {
            const mappedAthleteIds = mappedAthletes.map((mappedAthlete) => mappedAthlete.id);
            const newPeriods: AePeriod[] = activity.periods.map((period) => {
                const athleteIds = period.period_athletes
                    .filter((pa) => pa.athlete_id && mappedAthleteIds.includes(pa.athlete_id))
                    .map((a) => a.athlete_id);

                return {
                    id: period.id,
                    name: period.name,
                    startDateTime: period.start_time,
                    endDateTime: period.end_time,
                    duration: getSecondsDurationBetweenDates(period.start_time, period.end_time),
                    athleteIds,
                    activityId: activity.id,
                    athleteCount: athleteIds.length,
                    periodAthletes: period.period_athletes,
                };
            });

            // prevent render loop when there are no periods
            if (periods.length !== 0 || newPeriods.length !== 0) {
                actualSetPeriods(newPeriods);
            }
        }
    }, [activity, periods, mappedAthletes]);

    // Reset when switching activities
    const prevActivityId = usePrevious(activityId);
    React.useEffect(() => {
        if (prevActivityId !== activityId) {
            actualSetSelectedDeviceIds([]);
            actualSetSelectedPeriodIds([]);
            actualSetSelectedActivityAthleteIds([]);
            actualSetSelectedPeriodAthleteIds([]);
            setLastModified(undefined);
        }
    }, [prevActivityId, activityId, setClean]);

    // Remove activity ID from url if activity could not be loaded
    React.useEffect(() => {
        if (isLoadingError) {
            history.push('./');
        }
    }, [isLoadingError, history]);

    const runOverlapValidation = React.useCallback(
        (updatedPeriod: AePeriod) => {
            const issues: OverlapAlertIssue[] = [];
            athletePanelCards.forEach((card) => {
                if (updatedPeriod.athleteIds.includes(card.id)) {
                    const overlapIssues = checkPeriodOverlap(card, updatedPeriod, periods);
                    if (overlapIssues) {
                        issues.push(...overlapIssues);
                    }
                }
            });
            return issues;
        },
        [athletePanelCards, periods],
    );

    const validateAllPeriods = React.useCallback(
        (updatedPeriods: AePeriod[]) => {
            let isValid = true;
            for (let i = 0; i < updatedPeriods.length; i++) {
                const p = updatedPeriods[i];
                if (
                    isBefore(p.endDateTime, p.startDateTime) ||
                    !PERIOD_NAME_REGEX.test(p.name) ||
                    runOverlapValidation(p).length
                ) {
                    isValid = false;
                    break;
                }
            }

            setHasValidPeriods(isValid);
        },
        [runOverlapValidation, setHasValidPeriods],
    );

    // Re-check valid duration state after each period update, but without alerting
    React.useEffect(() => {
        validateAllPeriods(periods);
    }, [periods, validateAllPeriods]);

    return (
        <ActivityEditorContext.Provider
            value={{
                hasValidPeriods,
                setHasValidPeriods,
                isDirty,
                setClean,
                setDirty,
                activity: activity || undefined,
                periods,
                setPeriods,
                handlePeriodCreate,
                handlePeriodDelete,
                selectedDeviceIds,
                setSelectedDeviceIds,
                selectedPeriodIds,
                setSelectedPeriodIds,
                selectedActivityAthleteIds,
                setSelectedActivityAthleteIds,
                selectedPeriodAthleteIds,
                setSelectedPeriodAthleteIds,
                athleteIdsInSelectedPeriods,
                athletePanelCards,
                setAthletePanelCards,
                athletePanelCardIssues,
                setAthletePanelCardIssues,
                lastModified, // last time the activity was modified
                savingState,
                setSavingState,
                activePeriodTab: activePeriodTab,
                setActivePeriodTab: setActivePeriodTab,
            }}
        >
            {children}
        </ActivityEditorContext.Provider>
    );
};

export const useActivityEditor = () => React.useContext(ActivityEditorContext);
